'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';

import { classNames } from '@bytel/product-wall/helpers';
import {
    Align,
    Box,
    BoxContent,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Container,
    FlexItem,
    Link,
    Row,
    Rows,
    Section,
    Select,
    SelectOption,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { Breadcrumb as BreadcrumbComponent } from '@components/breadcrumb';
import { Faq } from '@components/faq';
import { Legals } from '@components/legals';

import { useMediaQuery } from '@hooks/use-media-query';
import { useOnScreen } from '@hooks/use-on-screen';

import { FAI_COMPARATOR_DESKTOP_LIMIT, FAI_COMPARATOR_MOBILE_LIMIT } from '@helpers/fai-comparator';
import { PAGE_SLUG } from '@helpers/partials';
import { paths } from '@helpers/path';
import { sendPageViewTracking } from '@helpers/tracking';

import {
    Comparator,
    ComparatorCell,
    ComparatorColumn,
    ComparatorColumns,
    ComparatorRow,
    ComparatorRowHead,
    ComparatorRows,
} from '@comparator/components';
import {
    ComparatorConfig,
    FaiProductsCharacteristicsType,
    groupFaiCharacteristics,
    useFaiProductSelection,
} from '@comparator/helpers';

import { FaiComparatorHead } from './fai-comparator-head';

interface FaiComparatorProps {
    productsCharacteristics: FaiProductsCharacteristicsType;
    comparatorConfig: ComparatorConfig;
}

export function FaiComparator({ productsCharacteristics, comparatorConfig }: Readonly<FaiComparatorProps>) {
    const isMobile = useMediaQuery('(max-width: 767px)');
    const { ref: comparatorHeadRef, isVisible: isComparatorHeadVisible } = useOnScreen(true);
    const { ref: comparatorRef, isVisible: isComparatorVisible } = useOnScreen(true);
    const searchParams = useSearchParams();

    const maxProducts: number = isMobile ? FAI_COMPARATOR_MOBILE_LIMIT : FAI_COMPARATOR_DESKTOP_LIMIT;

    const requestedSelection = useMemo(() => {
        const rangesParam = searchParams.get('ranges');

        if (rangesParam) {
            return rangesParam
                .split(',')
                .map((value) => value.trim())
                .filter((value) => value.includes(':'))
                .map((value) => {
                    const [rangeNg, commercialSeries] = value.split(':');

                    if (!rangeNg || !commercialSeries) {
                        return null;
                    }

                    return { rangeNg, commercialSeries };
                })
                .filter((selection) => selection !== null);
        }

        return [];
    }, [searchParams]);

    const preselectedSelection = useMemo(() => {
        if (!requestedSelection.length) {
            return [];
        }

        return requestedSelection
            .map((selection) =>
                productsCharacteristics.find(
                    (product) =>
                        product.rangeNg === selection.rangeNg &&
                        product.commercialSeries === selection.commercialSeries,
                ),
            )
            .filter((product) => !!product);
    }, [productsCharacteristics, requestedSelection]);

    const preselectedGencodes = useMemo(
        () => preselectedSelection.map((product) => product.gencode),
        [preselectedSelection],
    );

    const { selectedProducts, remainingSlots, handleChangeSelectedGencode, isOptionDisabled } = useFaiProductSelection(
        productsCharacteristics,
        maxProducts,
        preselectedGencodes,
    );

    const characteristics = useMemo(() => {
        return groupFaiCharacteristics(selectedProducts, comparatorConfig);
    }, [selectedProducts, comparatorConfig]);

    useEffect(() => {
        sendPageViewTracking(false, {
            page: 'Comparateur_offres',
            pageCategory: 'Information',
            universe: 'Boutique_fai',
            marketLine: 'Fai',
            isB2B: false,
        });
    }, []);

    useEffect(() => {
        // Position omnikit widget just before the comparator sticky bar
        window.omnikit = { insertBeforeId: 'sticky-bar' };
    }, []);

    return (
        <>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Title level={TitleLevels.ONE} marginless typo={TypographyAlign.TEXT_CENTERED}>
                    Comparez nos box internet
                </Title>
            </Section>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container>
                    <Rows>
                        <Row ref={comparatorHeadRef}>
                            <ComparatorColumns>
                                {selectedProducts.map((selectedProduct) => (
                                    <FaiComparatorHead key={selectedProduct.gencode} product={selectedProduct} />
                                ))}
                                {Array.from({ length: remainingSlots }).map((_, index) => (
                                    <ComparatorCell
                                        key={`empty-head-${index}`}
                                        className='is-aligned-center is-flex is-justified-center'
                                    >
                                        <Title level={TitleLevels.FIVE}>Sélectionnez une box</Title>
                                    </ComparatorCell>
                                ))}
                            </ComparatorColumns>
                        </Row>
                        <Row
                            className={classNames(
                                'py-small has-background-white',
                                isComparatorVisible
                                    ? 'is-sticky-top is-sticky-top-over-bottom z-level-4 '
                                    : 'is-sticky-top-hidden',
                            )}
                        >
                            <ComparatorColumns>
                                {selectedProducts.map((selectedProduct, index) => (
                                    <ComparatorColumn key={selectedProduct.gencode}>
                                        <Select
                                            id={`select-product-${index}`}
                                            selected={selectedProduct.gencode}
                                            onChange={({ selectValue }) =>
                                                handleChangeSelectedGencode(index, selectValue)
                                            }
                                        >
                                            {productsCharacteristics.map((product, i) => (
                                                <SelectOption
                                                    key={`${product.gencode}-${i}`}
                                                    value={product.gencode}
                                                    disabled={isOptionDisabled(product.gencode)}
                                                >
                                                    {product.name}
                                                </SelectOption>
                                            ))}
                                        </Select>
                                    </ComparatorColumn>
                                ))}
                                {Array.from({ length: remainingSlots }).map((_, index) => {
                                    const slotIndex = selectedProducts.length + index;

                                    return (
                                        <ComparatorColumn key={`placeholder-select-${index}`}>
                                            <Select
                                                id={`select-product-${slotIndex}`}
                                                selected=''
                                                onChange={({ selectValue }) =>
                                                    handleChangeSelectedGencode(slotIndex, selectValue)
                                                }
                                            >
                                                <SelectOption value=''>Sélectionner une offre</SelectOption>
                                                {productsCharacteristics.map((product, i) => (
                                                    <SelectOption
                                                        key={`${product.gencode}-placeholder-${i}`}
                                                        value={product.gencode}
                                                        disabled={isOptionDisabled(product.gencode)}
                                                    >
                                                        {product.name}
                                                    </SelectOption>
                                                ))}
                                            </Select>
                                        </ComparatorColumn>
                                    );
                                })}
                            </ComparatorColumns>
                        </Row>
                        <Row ref={comparatorRef}>
                            <Comparator>
                                {characteristics.map((characteristic) => (
                                    <ComparatorRows key={characteristic.key}>
                                        <ComparatorRowHead
                                            label={characteristic.name}
                                            imageUrl={characteristic.imageUrl}
                                        />
                                        {characteristic.items.map(({ items, name }, index) => (
                                            <ComparatorRow key={`${characteristic.key}-${index}`} name={name}>
                                                {items.map(({ key, children }) => (
                                                    <ComparatorCell key={key} className='is-flex is-aligned-center'>
                                                        {children}
                                                    </ComparatorCell>
                                                ))}
                                                {Array.from({ length: remainingSlots }).map((_, emptyIndex) => (
                                                    <ComparatorCell
                                                        key={`${characteristic.key}-${index}-empty-${emptyIndex}`}
                                                        className='is-flex is-aligned-center'
                                                    >
                                                        {' '}
                                                    </ComparatorCell>
                                                ))}
                                            </ComparatorRow>
                                        ))}
                                    </ComparatorRows>
                                ))}
                                <ComparatorRow>
                                    {selectedProducts.map((selectedProduct) => (
                                        <ComparatorColumn key={selectedProduct.gencode}>
                                            <Box shadowless>
                                                <BoxContent className='has-text-centered'>
                                                    <Link href={selectedProduct.detailsUrl}>Voir la fiche produit</Link>
                                                </BoxContent>
                                            </Box>
                                        </ComparatorColumn>
                                    ))}
                                    {Array.from({ length: remainingSlots }).map((_, index) => (
                                        <FlexItem key={`empty-details-link-${index}`} />
                                    ))}
                                </ComparatorRow>
                            </Comparator>
                        </Row>
                        <Row
                            id='sticky-bar'
                            className={isComparatorHeadVisible ? 'is-sticky-bottom-hidden' : 'is-sticky-bottom'}
                        >
                            <Box>
                                <BoxContent>
                                    <ComparatorColumns>
                                        {selectedProducts.map((selectedProduct) => (
                                            <ComparatorColumn key={selectedProduct.gencode}>
                                                <Button
                                                    variant={ButtonVariant.CONVERSION}
                                                    fullwidth
                                                    href={`${paths.eligibilityTest}?gamme=${selectedProduct.rangeNg}_${selectedProduct.commercialSeries}_${selectedProduct.technology.toLowerCase()}`}
                                                >
                                                    Choisir
                                                </Button>
                                            </ComparatorColumn>
                                        ))}
                                        {Array.from({ length: remainingSlots }).map((_, index) => (
                                            <FlexItem key={`empty-cta-${index}`} />
                                        ))}
                                    </ComparatorColumns>
                                </BoxContent>
                            </Box>
                        </Row>
                        <Row>
                            <Columns align={Align.CENTER}>
                                <Column desktopSize={10} widescreenSize={8} fullhdSize={7}>
                                    <Faq
                                        title={
                                            <>
                                                Des questions sur nos box internet&nbsp;?
                                                <br />
                                                C’est par ici&nbsp;!
                                            </>
                                        }
                                        titleAlt='Des questions sur nos box internet ? C’est par ici !'
                                        url={PAGE_SLUG.FAI_HOME}
                                    />
                                </Column>
                            </Columns>
                        </Row>
                        <Row>
                            <BreadcrumbComponent
                                elements={[
                                    {
                                        name: 'home',
                                        label: 'Accueil',
                                        href: '/',
                                    },
                                    {
                                        name: 'home FAI',
                                        label: 'Offres internet',
                                        href: paths.homeFai,
                                    },
                                    {
                                        name: 'comparator',
                                        label: 'Comparateur',
                                        href: '',
                                    },
                                ]}
                            />
                        </Row>
                        <Row>
                            <Legals id='fai-home-legals' url={PAGE_SLUG.FAI_HOME} />
                        </Row>
                    </Rows>
                </Container>
            </Section>
        </>
    );
}
